import { useEffect, useState } from 'react'

import { ApiError, api } from '../api'
import type {
  MeResponse,
  PortraitCardKind,
  TodayActiveSymbol,
  TodayChanges,
  TodayNextPage,
  TodayResponse,
  WeeklyCrossingDto,
} from '../../contracts/api'
import { Bases } from '../components/Bases'
import { FeedCard } from '../components/FeedCard'
import { JournalMark } from '../components/JournalMark'
import { SEAL_COLORS, withAlpha } from '../colors'
import { formatDate, inDaysLabel, plural } from '../dates'
import { useNav } from '../nav'
import './today.css'

const WEEKDAYS = ['воскресенье', 'понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота']

/** Длина волны Цолькина. В `changes` сервер шлёт только позицию, дни рисуем сами. */
const WAVE_LENGTH = 13

/** Подпись пересечения недели. Общая с экраном «Пересечения»: одна и та же
    карточка не может называться в ленте иначе, чем в своём разделе. */
export const WEEKLY_LABEL: Record<PortraitCardKind, string> = {
  support: 'Совпадение недели',
  contradiction: 'Парадокс недели',
  condition: 'Условие недели',
  unclear: 'Неясность недели',
}

export function todayLabel(iso: string): string {
  const [year, month, day] = iso.split('-').map(Number) as [number, number, number]
  const date = new Date(Date.UTC(year, month - 1, day))
  return `${WEEKDAYS[date.getUTCDay()] ?? ''} · ${formatDate(iso)}`
}

/**
 * «Сегодня» — лента: день, заметный сейчас символ, что меняется, новая страница
 * портрета, пересечение недели, пара, возврат к вопросу оракула. Карточки, у
 * которых сервер не прислал данных, не рисуются вовсе: пустых мест в ленте
 * быть не должно, иначе она начинает выглядеть сломанной.
 *
 * Каждая карточка ведёт в свой раздел — «Сегодня» ничего не объясняет до конца,
 * это витрина, а разбор живёт во вкладках.
 */
export function Today(_props: { me: MeResponse }) {
  const [data, setData] = useState<TodayResponse | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    api
      .get<TodayResponse>('/api/today')
      .then((result) => {
        if (!cancelled) setData(result)
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(cause instanceof ApiError ? cause.message : 'День пока не собрался')
      })
    return () => {
      cancelled = true
    }
  }, [])

  if (error) return <div className="state">{error}</div>
  if (!data) return <div className="state">Смотрим, какой сегодня день…</div>

  const subtitle = data.bases.map((base) => base.label).join(' · ')

  return (
    <div className="screen today">
      <div className="topbar">
        <div className="brand">АТЛАС</div>
        <div className="topnote">{todayLabel(data.date)}</div>
      </div>

      <div className="today-head">
        <h1 className="display">Сегодня</h1>
        <span className="today-sub">{subtitle}</span>
      </div>

      <div className="feed">
        <FeedCard label="Тема дня" labelColor="var(--gold)" className="day-card">
          <p className="day-theme">{data.card.theme}</p>

          <div className="day-block">
            <div className="lbl">Почему</div>
            <p>{data.card.why}</p>
          </div>
          <div className="day-block">
            <div className="lbl">Лучшее применение</div>
            <p>{data.card.useFor}</p>
          </div>
          <div className="day-block">
            <div className="lbl">Риск перегруза</div>
            <p>{data.card.risk}</p>
          </div>
          <div className="day-block">
            <div className="lbl">Маленькое действие</div>
            <p>{data.card.action}</p>
          </div>

          <Bases items={data.bases} variant="chips" />

          <JournalMark
            kind="day"
            refKey={data.date}
            ref={{ date: data.date, theme: data.card.theme }}
            keys={[]}
          />
        </FeedCard>

        {data.activeSymbol && <SymbolCard symbol={data.activeSymbol} />}
        <ChangesCard changes={data.changes} />
        {data.nextPage && <NextPageCard page={data.nextPage} />}
        {data.weekly && <WeeklyCard weekly={data.weekly} />}
        {data.coupleToday && <CoupleCard couple={data.coupleToday} />}
        {data.oracleReturn && <OracleReturnCard question={data.oracleReturn} />}
        {data.journalPrompt && <JournalCard prompt={data.journalPrompt} />}
      </div>
    </div>
  )
}

/** «Сейчас заметно» — одна личная сущность, которая сегодня в силе. */
function SymbolCard({ symbol }: { symbol: TodayActiveSymbol }) {
  const { navigate } = useNav()
  return (
    <FeedCard
      label="Сейчас заметно"
      labelColor={symbol.color}
      ariaLabel={`Сейчас заметно: ${symbol.title}`}
      onClick={() => navigate({ screen: { kind: 'symbol', id: symbol.id } })}
    >
      <div className="symbol-row">
        <span
          className="symbol-glyph"
          style={{
            background: withAlpha(symbol.color, 0.14),
            borderColor: withAlpha(symbol.color, 0.5),
            color: symbol.color,
          }}
          aria-hidden="true"
        >
          {symbol.glyph}
        </span>
        <span className="symbol-text">
          <span className="symbol-title">{symbol.title}</span>
          <span className="symbol-why">{symbol.whyNow}</span>
        </span>
      </div>
      <Bases items={symbol.bases} />
    </FeedCard>
  )
}

/** «Что меняется» — где мы в волне, что уже началось и что будет дальше. */
function ChangesCard({ changes }: { changes: TodayChanges }) {
  const { navigate } = useNav()
  const { wave, next, entered } = changes
  const color = SEAL_COLORS[wave.sealColor] ?? 'var(--gold)'
  const daysLeft = `${wave.daysLeft} ${plural(wave.daysLeft, 'день', 'дня', 'дней')}`

  return (
    <FeedCard
      label="Что меняется"
      ariaLabel="Что меняется — открыть Циклы"
      onClick={() => navigate({ tab: 'cycles' })}
    >
      <div className="wave-mini" aria-hidden="true">
        {Array.from({ length: WAVE_LENGTH }, (_, index) => {
          const day = index + 1
          const state = day === wave.position ? 'now' : day < wave.position ? 'past' : 'ahead'
          return (
            <span
              key={day}
              className={`wave-dot ${state}`}
              style={state === 'ahead' ? undefined : { background: color, borderColor: color }}
            />
          )
        })}
      </div>
      <p className="wave-caption">
        Волна «{wave.sealRu}» · день {wave.position}/{WAVE_LENGTH}, до конца {daysLeft}
      </p>

      {entered.length > 0 && (
        <div className="changes-list">
          {entered.map((item) => (
            <span className="changes-entered" key={`${item.kind}:${item.date}`}>
              <span className="changes-tick" aria-hidden="true">
                ✓
              </span>
              {item.label}
            </span>
          ))}
        </div>
      )}

      {next && (
        <p className="changes-next">
          Ближайший переход: {next.label}, {next.approximate ? '≈ ' : ''}
          {inDaysLabel(next.daysLeft)} ({formatDate(next.date)})
        </p>
      )}
    </FeedCard>
  )
}

/** «Новая страница Атласа» — следующий непрочитанный фрагмент портрета. */
function NextPageCard({ page }: { page: TodayNextPage }) {
  const { navigate } = useNav()
  return (
    <FeedCard
      label="Новая страница"
      labelColor="var(--gold)"
      ariaLabel={`Новая страница: ${page.title}`}
      onClick={() =>
        page.kind === 'chapter'
          ? navigate({ tab: 'me', screen: { kind: 'chapter', id: page.id, title: page.title } })
          : navigate({ screen: { kind: 'crossings' } })
      }
    >
      <p className="page-title">{page.title}</p>
      <p className="page-teaser">{page.teaser}</p>
    </FeedCard>
  )
}

/** Пересечение недели: где системы говорят об одном и где спорят. */
function WeeklyCard({ weekly }: { weekly: WeeklyCrossingDto }) {
  const { navigate } = useNav()
  return (
    <FeedCard
      label={WEEKLY_LABEL[weekly.kind]}
      ariaLabel={`${WEEKLY_LABEL[weekly.kind]}: ${weekly.title}`}
      onClick={() => navigate({ screen: { kind: 'crossings' } })}
    >
      <p className="weekly-title">{weekly.title}</p>
      {weekly.text && (
        <>
          <p className="weekly-text">{weekly.text.now}</p>
          <div className="day-block">
            <div className="lbl">Проверь на этой неделе</div>
            <p>{weekly.text.check}</p>
          </div>
        </>
      )}
      <Bases items={weekly.evidence} />
    </FeedCard>
  )
}

/** «Мы сегодня» — одна строка про ритмы пары. */
function CoupleCard({ couple }: { couple: NonNullable<TodayResponse['coupleToday']> }) {
  const { navigate } = useNav()
  return (
    <FeedCard label="Мы сегодня" ariaLabel="Мы сегодня — открыть вкладку «Мы»" onClick={() => navigate({ tab: 'we' })}>
      <p className="couple-line">{couple.line}</p>
      <Bases items={couple.bases} />
    </FeedCard>
  )
}

/** Возврат к вопросу оракула — в день, который человек сам назначил. */
function OracleReturnCard({ question }: { question: NonNullable<TodayResponse['oracleReturn']> }) {
  const { navigate } = useNav()
  return (
    <FeedCard
      label="Вернуться к вопросу"
      ariaLabel={`Вернуться к вопросу: ${question.question}`}
      onClick={() => navigate({ tab: 'oracle' })}
    >
      <p className="oracle-question">«{question.question}»</p>
      <p className="oracle-note">ты просил напомнить {formatDate(question.returnOn)}</p>
    </FeedCard>
  )
}

/**
 * Вчерашняя карточка дня, ещё не отмеченная. Отметка пишется прямо отсюда: до
 * журнала за этим никто не пойдёт, а через сутки человек уже не вспомнит.
 */
function JournalCard({ prompt }: { prompt: NonNullable<TodayResponse['journalPrompt']> }) {
  return (
    <FeedCard label="Вчера">
      <p className="journal-theme">{prompt.theme}</p>
      <JournalMark
        kind="day"
        refKey={prompt.day}
        ref={{ date: prompt.day, theme: prompt.theme }}
        keys={[]}
      />
    </FeedCard>
  )
}
